// PoC of the new Wellness Check-In flow.

import builderBackgroundVideoUrl from 'assets/videos/video-transition-wave-blur-bigger.mp4';
import backgroundVideoUrl from 'assets/videos/video-transition-wave-looping-bigger.mp4';
import {
  type FunctionComponent,
  type ReactNode,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import PageWithVideoBackground from 'components/PageWithVideoBackground';
import { ChatPageHeader } from 'components/ai-app';

import type { InputModeT } from 'components/ai-app/ChatInterface/LeftPanel';

import { useExperience } from 'hooks';

import { useAiBuildMeta } from 'hooks/useAiBuildMeta';
import {
  type ExperienceT,
  type InsightT,
  type TranscriptT,
  initExperienceData,
  toXmlTranscript,
  useGlobalState,
} from 'utils';

import builderBackgroundImageUrl from 'assets/images/bg-transitions-blur.png';

import backgroundImageUrl from 'assets/images/bg-transitions-looping.png';

import { Conversation } from './Conversation';
import { ExperienceBuilder } from './ExperienceBuilder';

import S from './style.scss';

// TODO: Perhaps, we don't need to set "EXPERIENCE_EDITOR" on this level,
// and should lower it down inside the ExperienceBuilder component.
type StateT = 'EXPERIENCE_PREPARATION' | 'EXPERIENCE_READY' | 'INTERVIEW';

type PropsT = {
  initAsEditor?: boolean;
};

type ApplyInitialEditorModeArgT = {
  experienceData: ExperienceT | null;
  forceEdit?: boolean;
  initAsEditor?: boolean;
  initialEditorModeApplied: boolean;
  setEditorMode: (editorMode: boolean) => void;
  setInitialEditorModeApplied: (value: boolean) => void;
};

function applyInitialEditorMode({
  experienceData,
  forceEdit,
  initAsEditor,
  initialEditorModeApplied,
  setEditorMode,
  setInitialEditorModeApplied,
}: ApplyInitialEditorModeArgT): void {
  if (initialEditorModeApplied || !initAsEditor || !experienceData) return;
  setInitialEditorModeApplied(true);
  if (experienceData.videos.length && !forceEdit) setEditorMode(false);
}

export const WellnessCheckIn: FunctionComponent<PropsT> = ({
  initAsEditor,
}) => {
  // eslint-disable-next-line react/hook-use-state
  const [startTimestamp] = useState(Date);

  const [query] = useSearchParams();
  const initialInputMode: InputModeT
    = query.get('mode') === 'text' ? 'keyboard' : 'mic';

  const experience = useExperience();

  const [editorMode, setEditorMode] = useState(initAsEditor);

  const [pageState, setPageState] = useState<StateT>(
    initAsEditor ? 'EXPERIENCE_READY' : 'INTERVIEW',
  );

  const [progress, setProgress] = useState(0);
  const [stageName, setStageName] = useState<string>();
  const [transcript, setTranscript] = useState<TranscriptT>();

  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Unauthenticated');

  const [audioSetup] = useGlobalState('ui.audioSetup');

  const [
    insights,
    setInsights,
    // BEWARE: The mapping here is from IDs of the messages prior to
    // the each insights array (i.e. the key specifies after which message
    // insights in that array should be rendered).
  ] = useState<Record<string, InsightT[]>>({});

  const [
    initialEditorModeApplied,
    setInitialEditorModeApplied,
  ] = useState(false);

  applyInitialEditorMode({
    experienceData: experience.data?.experience ?? null,
    forceEdit: query.get('edit') === '1',
    initAsEditor,
    initialEditorModeApplied,
    setEditorMode,
    setInitialEditorModeApplied,
  });

  const restartCheckIn = () => {
    experience.set({
      checkInId: null,
      experience: initExperienceData(),
      sessionKind: null,
    });
    setEditorMode(false);
    setPageState('INTERVIEW');
    setProgress(0);
    setStageName(undefined);
    setTranscript(undefined);
    setInsights({});
  };

  let insightsArray: InsightT[] = [];
  for (const array of Object.values(insights)) {
    insightsArray = insightsArray.concat(array);
  }

  const buildMeta = useAiBuildMeta(
    // BEWARE: The timestamp below should be stable, as if it changes on each
    // render, it will re-trigger the underlying API & AI call to get the meta
    // data to display in UI.
    transcript ? toXmlTranscript(transcript, startTimestamp).xml : '',
  );

  const onSkip = pageState === 'EXPERIENCE_READY' ? undefined : () => {
    setEditorMode(true);
    setStageName(undefined);
    setPageState('EXPERIENCE_READY');
  };

  let content: ReactNode;
  let subTitle: string;
  let title: string;
  switch (pageState) {
    case 'EXPERIENCE_PREPARATION':
    case 'EXPERIENCE_READY':
      content = (
        <ExperienceBuilder
          buildMeta={buildMeta}
          editorMode={editorMode ?? false}
          experience={experience}
          initLikeEditor={pageState === 'EXPERIENCE_READY'}
          insights={insightsArray}
          onExit={restartCheckIn}
          onProgress={setProgress}
          ready={pageState === 'EXPERIENCE_READY'}
          setEditorMode={setEditorMode}
        />
      );
      subTitle = 'Personalizing for you';
      title = 'Building Experience';
      break;
    case 'INTERVIEW':
      content = (
        <Conversation
          experience={experience}
          initialInputMode={initialInputMode}
          insights={insights}
          onProgress={setProgress}
          onSignal={(signal, transcript2) => {
            setPageState(signal);
            if (transcript2 !== undefined) setTranscript(transcript2);
          }}
          setInsights={setInsights}
          setStageName={setStageName}
        />
      );
      subTitle = 'Wellness Check-in';
      title = 'Soul Sanctuary';
      break;
    default: throw Error('Unexpected case');
  }

  let containerClassName = S.container;
  if (editorMode) containerClassName += ` ${S.tollPage}`;

  const isBuilder = pageState !== 'INTERVIEW';

  let videoUrl: string | undefined = builderBackgroundVideoUrl;
  if (editorMode) videoUrl = undefined;
  else if (isBuilder) videoUrl = backgroundVideoUrl;

  return (
    <PageWithVideoBackground
      fallbackImageUrl={
        isBuilder ? builderBackgroundImageUrl : backgroundImageUrl
      }
      hideMobileToggle={editorMode}
      videoUrl={videoUrl}
    >
      {
        pageState === 'INTERVIEW' ? (
          <video
            muted
            playsInline
            preload="auto"
            style={{ display: 'none' }}
          >
            <source src={backgroundVideoUrl} />
          </video>
        ) : null
      }
      {audioSetup
        ? <div />
        : (
          <div className={containerClassName}>
            {pageState === 'INTERVIEW' ? (
              <ChatPageHeader
                onSkip={onSkip}
                progress={progress}
                stage={stageName}
                subTitle={subTitle}
                title={title}
              />
            ) : null}
            {content}
          </div>
        )}
    </PageWithVideoBackground>
  );
};
